import React, { useEffect, useRef, useState } from "react";
import { Constants } from "@eagerdog/constants";
import { Link } from "react-router-dom";
import { IFeedPostItem, postShareDescription, postShareTitle } from "@eagerdog/interfaces";
import moment from "moment";

import { helperService } from "src/services/helper.service";
import { apiService } from "src/services/api.service";
import { localStorageService } from "src/services/local-storage.service";
import { toast } from "src/components/Toast/ToastManager";
import { Modal, useModal } from "src/components/Modal/Modal";
import ShareModal from "src/components/ShareModal/ShareModal";
import FavoriteClubModal from "src/components/FavoriteClubModal/FavoriteClubModal";
import PostCardMenu, { EditedMarker, ReportedPlaceholder, UnderReviewChip, useRequireLogin } from "./PostCardMenu";
import ActivityBlock from "./ActivityBlock";
import { postPath, postShareUrl } from "./postLinks";

import styles from "./FeedCard.module.scss";

interface IProps {
    post: IFeedPostItem;
    onChange: () => void;
    /** True on /p/:id — the timestamp is plain text instead of a link to itself. */
    isPermalink?: boolean;
}

const ClubPostCard: React.FC<IProps> = ({ post, onChange, isPermalink = false }) => {
    const [user, setUser] = useState(helperService.getUser());
    const [liked, setLiked] = useState(!!post.liked_by_viewer);
    const [likeCount, setLikeCount] = useState(post.like_count || 0);
    const liking = useRef(false);
    const [reported, setReported] = useState(false);
    const shareModal = useModal();
    const favoriteModal = useModal();
    const requireLogin = useRequireLogin();

    const isFollowing = (user.favorite_clubs || []).includes(post.author_id);
    const clubName = post.author_display_name || "this club";
    const underReview = post.moderation_status === "under_review";

    // Long club messages are clamped to a few lines with a "See more" toggle.
    const bodyRef = useRef<HTMLDivElement>(null);
    const [bodyOverflows, setBodyOverflows] = useState(false);
    const [bodyExpanded, setBodyExpanded] = useState(false);
    useEffect(() => {
        const el = bodyRef.current;
        if (!el) return;
        setBodyOverflows(el.scrollHeight > el.clientHeight + 1);
    }, [post.body]);

    const toggleLike = () => {
        if (!requireLogin()) return;
        if (liking.current) return;
        liking.current = true;
        const nextLiked = !liked;
        setLiked(nextLiked);
        setLikeCount((c) => c + (nextLiked ? 1 : -1));
        apiService.togglePostLike(post._id).then((result) => {
            if (typeof result?.like_count === "number") {
                setLiked(!!result.liked);
                setLikeCount(result.like_count);
            }
        }).catch(() => {
            setLiked(!nextLiked);
            setLikeCount((c) => c + (nextLiked ? -1 : 1));
        }).finally(() => {
            liking.current = false;
        });
    };

    // Follow / unfollow live in the "⋯" menu: a club's posts are only in your
    // feed because you follow it, so the bottom row keeps Like and Share only.
    const toggleFollow = () => {
        if (!requireLogin()) return;
        const nextFavorites = isFollowing
            ? (user.favorite_clubs || []).filter((id) => id !== post.author_id)
            : [...(user.favorite_clubs || []), post.author_id];

        apiService.updateSelf({ favorite_clubs: nextFavorites }).then((updatedUser) => {
            localStorageService.setItem(localStorageService.USER, JSON.stringify(updatedUser));
            setUser(updatedUser);
            if (isFollowing) {
                toast.show({ title: "Unfollowed", content: `You no longer follow ${clubName}.`, duration: 4000, type: "success" });
                // The home feed drops this club's posts on reload.
                onChange();
            }
        }).catch(() => {
            toast.show({ title: "Couldn't update follow", content: "Please try again.", duration: 6000, type: "fail" });
        });

        // Same as the Clubs listing: add the favorite first, then offer the club's direct communications.
        if (!isFollowing) favoriteModal.toggle();
    };

    // The modal writes the opt-in to the user; re-read so the card reflects it.
    const closeFavoriteModal = () => {
        favoriteModal.toggle();
        setUser(helperService.getUser());
    };

    const initial = (post.author_display_name || "?").charAt(0).toUpperCase();
    const clubLink = `/k/${post.author_id}`;
    const isEventPublished = post.post_type === Constants.post_type.event_published;
    // Canonical permalink for every channel, so the link unfurls as this post.
    const shareUrl = postShareUrl(post);
    const shareTitle = postShareTitle(post);
    const timestamp = moment(post.created_at).fromNow();

    if (reported) return <ReportedPlaceholder />;

    return (
        <div className={styles.FeedCard}>
            <div className={styles.header}>
                <Link to={clubLink} className={styles.avatar}>
                    {post.author_image_url
                        ? <img src={post.author_image_url} alt={post.author_display_name} />
                        : <span>{initial}</span>}
                </Link>
                <div className={styles.headerText}>
                    <span className={styles.authorName}>
                        <Link to={clubLink} className={styles.nameLink}>{post.author_display_name}</Link>
                        {isEventPublished && <span className={styles.plain}> published a new event</span>}
                    </span>
                    <span className={styles.metaRow}>
                        {isPermalink
                            ? timestamp
                            : <Link to={postPath(post)} className={styles.timeLink}>{timestamp}</Link>}
                        <EditedMarker post={post} />
                    </span>
                </div>
                {underReview && <UnderReviewChip />}
                <PostCardMenu
                    post={post}
                    onChange={onChange}
                    onReported={() => setReported(true)}
                    shareUrl={shareUrl}
                    extraOptions={[{ label: isFollowing ? "Unfollow club" : "Follow club", onClick: toggleFollow }]}
                />
            </div>
            {post.body && (
                <div>
                    <div ref={bodyRef} className={`${styles.body} ${bodyExpanded ? "" : styles.clamped}`}>{post.body}</div>
                    {(bodyOverflows || bodyExpanded) && (
                        <button type="button" className={styles.seeMore} onClick={() => setBodyExpanded((v) => !v)}>
                            {bodyExpanded ? "See less" : "See more"}
                        </button>
                    )}
                </div>
            )}
            {post.activity && <ActivityBlock post={post} />}
            {/* An event post's image is the flyer, rendered inside the event card. */}
            {!isEventPublished && post.media && post.media[0] && (
                <div className={styles.media}>
                    {post.media[0].type === Constants.post_media_type.video
                        ? <video src={post.media[0].url} controls />
                        : <img src={post.media[0].url} alt="" />}
                </div>
            )}
            {(likeCount > 0 || post.share_count > 0) && (
                <div className={styles.countsRow}>
                    {likeCount > 0 && <span>{likeCount} Like{likeCount === 1 ? "" : "s"}</span>}
                    {post.share_count > 0 && <span>{post.share_count} Share{post.share_count === 1 ? "" : "s"}</span>}
                </div>
            )}
            <div className={styles.actionsRow}>
                <button className={liked ? styles.active : ""} onClick={toggleLike} disabled={underReview}>Like</button>
                <button onClick={() => { if (requireLogin()) shareModal.toggle(); }} disabled={underReview}>Share</button>
            </div>
            <Modal
                className="ShareModalContainer"
                isShown={shareModal.isShown}
                hide={shareModal.toggle}
                modalContent={
                    <ShareModal
                        eventTitle={shareTitle}
                        shareText={postShareDescription(post) || undefined}
                        eventUrl={shareUrl}
                        shareType="post"
                        onClose={shareModal.toggle}
                        onShare={(channel) => {
                            apiService.recordPostShare(post._id, channel).then(onChange).catch(() => {});
                        }}
                    />
                }
            />
            <FavoriteClubModal
                isShown={favoriteModal.isShown}
                hide={closeFavoriteModal}
                club={{ _id: post.author_id, club_name: post.author_display_name || "Club" }}
            />
        </div>
    );
};

export default ClubPostCard;
